import React, { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useFeatureValue } from '@growthbook/growthbook-react'

import {
  selectCurrentVariantCohort,
  selectLanguage,
} from 'root-redux/selects/common'

import { useActiveCohortIdentifier } from 'hooks/useActiveCohortIdentifier'
import { useCohortToUse } from 'hooks/useCohortToUse'
import { useVatInfo } from 'hooks/useHasVatInfo'
import { useIsCancelOfferRoute } from 'hooks/useIsCancelOfferRoute'

import { getDynamicDiscountText } from 'modules/purchase/helpers/rootHelpers'
import { usePurchaseStore } from 'modules/purchase/hooks'
import { useGetPriceWithCurrencyFormatting } from 'modules/purchase/hooks/useGetPriceWithCurrencyFormatting'
import { usePriceWithVatAdjustment } from 'modules/purchase/hooks/usePriceWithVatAdjustment'
import { useSummarizedSubscriptionData } from 'modules/purchase/hooks/useSummarizedSubscriptionData'
import {
  selectDynamicDiscount,
  selectSubscription,
} from 'modules/purchase/redux/selects/common'

import {
  ABCDTestVariants,
  BillingCycle,
  CURRENCY_TO_ICON,
  GROWTHBOOK_EXPERIMENT,
  I18N_CONTEXT_COHORT_MAP,
  LUVLY_REF6_CHILD_COHORT,
  Locale,
  SUBSCRIPTION_PERIOD_TO_DURATION_MAP,
} from 'root-constants'

import { StyledCheckoutPurchaseDetails as S } from './CheckoutPurchaseDetails.styles'

export const CheckoutPurchaseDetails: React.FC = () => {
  const { t } = useTranslation()
  const dynamicDiscount = useSelector(selectDynamicDiscount)
  const selectedSubscription = useSelector(selectSubscription)
  const { isDynamicDiscountCohort, isLuvlyChinCohort } =
    useActiveCohortIdentifier()
  const cohortToUse = useCohortToUse()
  const hasVatInfo = useVatInfo()
  const { currency, periodQuantity } = usePurchaseStore()
  const isCancelOfferRoute = useIsCancelOfferRoute()
  const language = useSelector(selectLanguage)
  const cohort = useSelector(selectCurrentVariantCohort) as string

  const { getPriceWithVatAdjustment } = usePriceWithVatAdjustment()
  const { getPriceWithCurrencyFormatting } = useGetPriceWithCurrencyFormatting()
  const summarizedSubscription = useSummarizedSubscriptionData()

  const refNewPricingVariant = useFeatureValue(
    GROWTHBOOK_EXPERIMENT.LV_1530,
    ABCDTestVariants.VARIANT_A,
  )

  const isNoTrialSubscription = useMemo(() => {
    return (
      cohort === LUVLY_REF6_CHILD_COHORT ||
      (refNewPricingVariant === ABCDTestVariants.VARIANT_C &&
        periodQuantity === BillingCycle.ONE_MONTH)
    )
  }, [cohort, refNewPricingVariant, periodQuantity])

  const isArabicLocale = useMemo(() => {
    return language === Locale.ARABIC
  }, [language])

  if (!selectedSubscription) {
    return null
  }

  const currencyLabel = isArabicLocale
    ? CURRENCY_TO_ICON[currency.toUpperCase()]
    : currency.toUpperCase()

  const renderDiscountText = () => {
    if (isDynamicDiscountCohort && !isNoTrialSubscription) {
      return getDynamicDiscountText({
        discountPercentage: summarizedSubscription.discountPercentage,
        isCancelOffer: isCancelOfferRoute,
        cohort: cohortToUse,
        discountText: dynamicDiscount?.checkout,
      })
    }

    return (
      <Trans
        i18nKey="checkoutExtended.introductoryDiscount"
        values={{
          discountPercentage: summarizedSubscription.discountPercentage,
        }}
      />
    )
  }

  const getFormattedAmountLabel = (amount: number) => {
    const formattedValue = getPriceWithCurrencyFormatting(amount.toFixed(2))
    if (isArabicLocale) {
      return `${currencyLabel}${formattedValue}`
    }
    return `${formattedValue} ${currencyLabel}`
  }

  const getFormattedAmountLabelMinus = (amount: number) => {
    const formattedValue = getPriceWithCurrencyFormatting(amount.toFixed(2))
    if (isArabicLocale) {
      return `-${currencyLabel}${formattedValue}`
    }
    return `-${formattedValue} ${currencyLabel}`
  }

  const getDailyPriceLabel = (dailyPrice: number) => {
    if (isArabicLocale) {
      return `${currencyLabel}${getPriceWithVatAdjustment(dailyPrice)}`
    }
    return `${getPriceWithVatAdjustment(dailyPrice)} ${currencyLabel}`
  }

  const getTotalAmountLabel = () => {
    if (!hasVatInfo) return t('checkoutExtended.total')

    return isLuvlyChinCohort
      ? t('checkoutExtended.totalWithVat2')
      : t('checkoutExtended.totalWithVat')
  }
  const getTotalAmountValue = () => {
    let context = selectedSubscription.trialPrices.durationDays
    if (isNoTrialSubscription) {
      context = SUBSCRIPTION_PERIOD_TO_DURATION_MAP[periodQuantity]
    }
    return (
      <Trans
        i18nKey="checkoutExtended.totalAmount"
        values={{
          totalAmount: getPriceWithCurrencyFormatting(
            selectedSubscription.trialPrices.fullPrice ||
              selectedSubscription.mainPrices.fullPrice,
          ),
          currency: currencyLabel,
          context,
        }}
      />
    )
  }

  return (
    <>
      <S.PriceDescription>
        <S.PriceInfo>
          <S.PriceBlockText>
            {t('checkoutExtended.personalizedPlan', {
              context: I18N_CONTEXT_COHORT_MAP[cohortToUse],
            })}
          </S.PriceBlockText>
          <S.PriceBlockValue>
            {getFormattedAmountLabel(summarizedSubscription.totalAmount)}
          </S.PriceBlockValue>
        </S.PriceInfo>

        <S.PriceInfo>
          <S.PriceBlockText>{renderDiscountText()}</S.PriceBlockText>
          <S.TotalDiscount>
            {getFormattedAmountLabelMinus(
              summarizedSubscription.discountAmount,
            )}
          </S.TotalDiscount>
        </S.PriceInfo>
      </S.PriceDescription>
      <S.DailyPayment>
        <S.DailyText>{t('checkoutExtended.totalPerDay')}</S.DailyText>
        <S.DailyAmount>
          {getDailyPriceLabel(
            isNoTrialSubscription
              ? selectedSubscription.mainPrices.daily
              : selectedSubscription.trialPrices.daily,
          )}
        </S.DailyAmount>
      </S.DailyPayment>
      <S.TotalPayment>
        <S.TotalDescription>
          <S.TotalText>
            <Trans
              i18nKey={getTotalAmountLabel()}
              components={[<br />, <strong />]}
            />
          </S.TotalText>
          <S.TotalAmount>{getTotalAmountValue()}</S.TotalAmount>
        </S.TotalDescription>
        <S.SaveText>
          <Trans
            i18nKey="checkoutExtended.savedAmount"
            values={{
              savedAmount: getPriceWithCurrencyFormatting(
                summarizedSubscription.discountAmount.toFixed(2),
              ),
              currency: currencyLabel,
              discountPercentage: summarizedSubscription.discountPercentage,
            }}
          />
        </S.SaveText>
      </S.TotalPayment>
    </>
  )
}
